import type { Race } from '@character-creator/types';

/**
 * Ignus Species Data
 *
 * Contains all species available in the Ignus ruleset.
 * This includes:
 * - Modified versions of SRD races with Ignus-specific subraces
 * - Completely new Ignus species (Skyborn, Ignan)
 *
 * When the Ignus ruleset is active, these override the SRD versions.
 */
export const ignusRaces: Race[] = [
  // =============================================================================
  // DWARF - Ignus version with City, Forest, Iron subraces
  // =============================================================================
  {
    id: 'dwarf',
    name: 'Dwarf',
    description:
      'Bold and hardy, dwarves are known as skilled warriors, miners, and workers of stone and metal.',
    abilityBonuses: [{ ability: 'constitution', bonus: 2 }],
    age: "Dwarves mature at the same rate as humans, but they're considered young until they reach the age of 50. On average, they live about 350 years.",
    size: 'medium',
    sizeDescription:
      'Dwarves stand between 4 and 5 feet tall and average about 150 pounds.',
    speed: 25,
    languages: ['Common', 'Dwarvish'],
    traits: [
      {
        id: 'darkvision',
        name: 'Darkvision',
        description:
          'You can see in dim light within 60 feet of you as if it were bright light, and in darkness as if it were dim light.',
      },
      {
        id: 'dwarven-resilience',
        name: 'Dwarven Resilience',
        description:
          'You have advantage on saving throws against poison, and you have resistance against poison damage.',
      },
      {
        id: 'dwarven-combat-training',
        name: 'Dwarven Combat Training',
        description:
          'You have proficiency with the battleaxe, handaxe, light hammer, and warhammer.',
      },
      {
        id: 'tool-proficiency',
        name: 'Tool Proficiency',
        description:
          "You gain proficiency with the artisan's tools of your choice: smith's tools, brewer's supplies, tinker's tools, or mason's tools.",
      },
      {
        id: 'stonecunning',
        name: 'Stonecunning',
        description:
          'Whenever you make an Intelligence (History) check related to the origin of stonework, you are considered proficient in the History skill and add double your proficiency bonus to the check.',
      },
    ],
    subraces: [
      {
        id: 'hill-dwarf',
        name: 'Hill Dwarf',
        description:
          'As a hill dwarf, you have keen senses, deep intuition, and remarkable resilience.',
        abilityBonuses: [{ ability: 'wisdom', bonus: 2 }],
        traits: [
          {
            id: 'dwarven-toughness',
            name: 'Dwarven Toughness',
            description:
              'Your hit point maximum increases by 1, and it increases by 1 every time you gain a level.',
          },
        ],
      },
      {
        id: 'city-dwarf',
        name: 'City Dwarf',
        description:
          "As a City Dwarf, you grew up in the hustle and bustle, dog-eat-dog city streets. You're a shrewd negotiator, have a sharp eye, and a remarkable ability to take care of yourself.",
        abilityBonuses: [{ ability: 'charisma', bonus: 2 }],
        traits: [
          {
            id: 'street-smarts',
            name: 'Street Smarts',
            description: 'You gain proficiency in both the Perception and Survival skills.',
          },
          {
            id: 'shrewd-negotiator',
            name: 'Shrewd Negotiator',
            description: 'You gain proficiency in both the Persuasion and Deception skills.',
          },
        ],
      },
      {
        id: 'forest-dwarf',
        name: 'Forest Dwarf',
        description: "As a forest dwarf, you're one with nature and fleet of foot.",
        abilityBonuses: [{ ability: 'dexterity', bonus: 2 }],
        traits: [
          {
            id: 'woodswise',
            name: 'Woodswise',
            description: 'You gain proficiency in both the Survival and Nature skills.',
          },
          {
            id: 'surefooted',
            name: 'Surefooted',
            description: 'You can ignore difficult terrain.',
          },
          {
            id: 'treefriend',
            name: 'Treefriend',
            description: 'You have a climbing speed equal to your walking speed.',
          },
        ],
      },
      {
        id: 'iron-dwarf',
        name: 'Iron Dwarf',
        description:
          'The iron dwarves of Ignus live in isolated clans deep in the mountains and forests of the world. They are miners, masons, and smiths without parallel.',
        abilityBonuses: [{ ability: 'strength', bonus: 2 }],
        traits: [
          {
            id: 'iron-skin',
            name: 'Iron Skin',
            description: 'Your base AC is 12 and you have proficiency with heavy armor.',
          },
          {
            id: 'child-of-iron',
            name: 'Child of Iron',
            description:
              'Your body and mind have come to resemble the metal and stone that your people mine. You are immune to the charmed, poisoned, and stunned conditions.',
          },
        ],
      },
    ],
    source: 'ignus',
  },

  // =============================================================================
  // ELF - Ignus version with Low, Aurora subraces
  // =============================================================================
  {
    id: 'elf',
    name: 'Elf',
    description:
      'Elves are a magical people of otherworldly grace, living in the world but not entirely part of it.',
    abilityBonuses: [{ ability: 'dexterity', bonus: 2 }],
    age: 'Although elves reach physical maturity at about the same age as humans, the elven understanding of adulthood goes beyond physical growth to encompass worldly experience. An elf typically claims adulthood and an adult name around the age of 100 and can live to be 750 years old.',
    size: 'medium',
    sizeDescription:
      'Elves range from under 5 to over 6 feet tall and have slender builds.',
    speed: 30,
    languages: ['Common', 'Elvish'],
    traits: [
      {
        id: 'darkvision',
        name: 'Darkvision',
        description:
          'You can see in dim light within 60 feet of you as if it were bright light, and in darkness as if it were dim light.',
      },
      {
        id: 'keen-senses',
        name: 'Keen Senses',
        description: 'You have proficiency in the Perception skill.',
      },
      {
        id: 'fey-ancestry',
        name: 'Fey Ancestry',
        description:
          "You have advantage on saving throws against being charmed, and magic can't put you to sleep.",
      },
      {
        id: 'trance',
        name: 'Trance',
        description:
          "Elves don't need to sleep. Instead, they meditate deeply, remaining semiconscious, for 4 hours a day.",
      },
    ],
    subraces: [
      {
        id: 'high-elf',
        name: 'High Elf',
        description:
          'As a high elf, you have a keen mind and a mastery of at least the basics of magic.',
        abilityBonuses: [{ ability: 'intelligence', bonus: 1 }],
        traits: [
          {
            id: 'elf-weapon-training',
            name: 'Elf Weapon Training',
            description:
              'You have proficiency with the longsword, shortsword, shortbow, and longbow.',
          },
          {
            id: 'cantrip',
            name: 'Cantrip',
            description:
              'You know one cantrip of your choice from the wizard spell list. Intelligence is your spellcasting ability for it.',
          },
          {
            id: 'extra-language',
            name: 'Extra Language',
            description: 'You can speak, read, and write one extra language of your choice.',
          },
        ],
      },
      {
        id: 'low-elf',
        name: 'Low Elf',
        description:
          'The Low Elves of Ignus split off from the High Elves centuries before The Destruction. They rejected the high-minded, smugly superior stance held by their High Elf cousins and chose to mingle with the other species. Low elves tend to be more muscular with rounder features, physical and friendly.',
        abilityBonuses: [{ ability: 'strength', bonus: 1 }],
        traits: [
          {
            id: 'low-elf-weapon-training',
            name: 'Low Elf Weapon Training',
            description:
              'You have proficiency with the greatsword, greataxe, warhammer, and longbow.',
          },
          {
            id: 'people-person',
            name: 'People Person',
            description: 'You gain proficiency in both the Persuasion and Intimidation skills.',
          },
          {
            id: 'powerful-build',
            name: 'Powerful Build',
            description:
              'You count as one size larger when determining your carrying capacity and the weight you can push, drag, or lift.',
          },
        ],
      },
      {
        id: 'aurora-elf',
        name: 'Aurora Elf',
        description:
          'The Aurora Elves of Ignus are descendants of an experimental strain of elvenkind developed by the Raathan Archive before The Destruction. Modern Aurora Elves tend to live in isolated colonies and are rarely seen in society. They have overly large, extremely pale eyes and hair in shades of blue, green, and purple.',
        abilityBonuses: [{ ability: 'wisdom', bonus: 1 }],
        traits: [
          {
            id: 'telepathic-speech',
            name: 'Telepathic Speech',
            description:
              "You can speak telepathically to any creature within 30 feet of you. You don't need to share a language with the creature for it to understand your telepathy, but it must be able to understand at least one language.",
          },
          {
            id: 'magic-resistance',
            name: 'Magic Resistance',
            description:
              'You have advantage on saving throws against spells and other magical effects.',
          },
          {
            id: 'aurora-flicker',
            name: 'Aurora Flicker',
            description:
              'As a bonus action, you can magically teleport up to 30 feet to an unoccupied space you can see. You can use this trait a number of times equal to your proficiency bonus, regaining all expended uses when you finish a long rest.',
          },
        ],
      },
    ],
    source: 'ignus',
  },

  // =============================================================================
  // HALFLING - Ignus version with Riverkeen, Feral subraces
  // =============================================================================
  {
    id: 'halfling',
    name: 'Halfling',
    description:
      'The diminutive halflings survive in a world full of larger creatures by avoiding notice or, barring that, avoiding offense.',
    abilityBonuses: [{ ability: 'dexterity', bonus: 2 }],
    age: 'A halfling reaches adulthood at the age of 20 and generally lives into the middle of his or her second century.',
    size: 'small',
    sizeDescription: 'Halflings average about 3 feet tall and weigh about 40 pounds.',
    speed: 25,
    languages: ['Common', 'Halfling'],
    traits: [
      {
        id: 'lucky',
        name: 'Lucky',
        description:
          'When you roll a 1 on the d20 for an attack roll, ability check, or saving throw, you can reroll the die and must use the new roll.',
      },
      {
        id: 'brave',
        name: 'Brave',
        description: 'You have advantage on saving throws against being frightened.',
      },
      {
        id: 'halfling-nimbleness',
        name: 'Halfling Nimbleness',
        description: 'You can move through the space of any creature that is of a size larger than yours.',
      },
    ],
    subraces: [
      {
        id: 'lightfoot',
        name: 'Lightfoot',
        description:
          'As a lightfoot halfling, you can easily hide from notice, even using other people as cover. Lightfoots are more prone to wanderlust than other halflings.',
        abilityBonuses: [{ ability: 'charisma', bonus: 1 }],
        traits: [
          {
            id: 'naturally-stealthy',
            name: 'Naturally Stealthy',
            description:
              'You can attempt to hide even when you are obscured only by a creature that is at least one size larger than you.',
          },
        ],
      },
      {
        id: 'riverkeen',
        name: 'Riverkeen',
        description:
          'Found working docks and boats all across Ignus, riverkeen halflings are most at ease in and near the water. Cheerful and friendly, they are often compared to otters with slender bodies and smooth, straight, almost fur-like hair.',
        abilityBonuses: [{ ability: 'constitution', bonus: 1 }],
        traits: [
          {
            id: 'wave-rider',
            name: 'Wave Rider',
            description:
              "You have a swimming speed of 30 feet. To use this speed, you can't be wearing medium or heavy armor.",
          },
          {
            id: 'underwater-worker',
            name: 'Underwater Worker',
            description:
              'You can hold your breath for a number of minutes equal to 2 + double your Constitution modifier.',
          },
        ],
      },
      {
        id: 'feral',
        name: 'Feral',
        description:
          'Solitary and secretive, the feral halflings of Ignus are considered by many to be merely a myth. They are quick, clever, nearly impossible to catch, and fierce when backed into a corner.',
        abilityBonuses: [{ ability: 'dexterity', bonus: 1 }],
        traits: [
          {
            id: 'feral-naturally-stealthy',
            name: 'Naturally Stealthy',
            description: 'You have proficiency in stealth.',
          },
          {
            id: 'invisible-stalker',
            name: 'Invisible Stalker',
            description:
              'As a bonus action, you can turn invisible until the start of your next turn or until you attack, make a damage roll, or force someone to make a saving throw. You can use this trait a number of times equal to your proficiency bonus, regaining all expended uses after a long rest.',
          },
          {
            id: 'feral-savagery',
            name: 'Feral Savagery',
            description:
              'Once per round, when you damage a creature with a weapon or spell attack, you can deal extra damage equal to 1d6 + your proficiency bonus. You can use this trait a number of times equal to your proficiency bonus, regaining all uses once you finish a long rest.',
          },
        ],
      },
    ],
    source: 'ignus',
  },

  // =============================================================================
  // GNOME - Ignus version with Sky, Night subraces
  // =============================================================================
  {
    id: 'gnome',
    name: 'Gnome',
    description:
      'A gnome\'s energy and enthusiasm for living shines through every inch of their tiny bodies.',
    abilityBonuses: [{ ability: 'intelligence', bonus: 2 }],
    age: 'Gnomes mature at the same rate humans do, and most are expected to settle down into an adult life by around age 40. They can live 350 to almost 500 years.',
    size: 'small',
    sizeDescription: 'Gnomes are between 3 and 4 feet tall and average about 40 pounds.',
    speed: 25,
    languages: ['Common', 'Gnomish'],
    traits: [
      {
        id: 'darkvision',
        name: 'Darkvision',
        description:
          'You can see in dim light within 60 feet of you as if it were bright light, and in darkness as if it were dim light.',
      },
      {
        id: 'gnome-cunning',
        name: 'Gnome Cunning',
        description:
          'You have advantage on all Intelligence, Wisdom, and Charisma saving throws against magic.',
      },
    ],
    subraces: [
      {
        id: 'rock-gnome',
        name: 'Rock Gnome',
        description:
          'As a rock gnome, you have a natural inventiveness and hardiness beyond that of other gnomes.',
        abilityBonuses: [{ ability: 'constitution', bonus: 1 }],
        traits: [
          {
            id: 'artificers-lore',
            name: "Artificer's Lore",
            description:
              'Whenever you make an Intelligence (History) check related to magic items, alchemical objects, or technological devices, you can add twice your proficiency bonus.',
          },
          {
            id: 'tinker',
            name: 'Tinker',
            description:
              "You have proficiency with artisan's tools (tinker's tools). Using those tools, you can spend 1 hour and 10 gp worth of materials to construct a Tiny clockwork device.",
          },
        ],
      },
      {
        id: 'sky-gnome',
        name: 'Sky Gnome',
        description:
          'The Sky gnomes actually live in the treetops. As a sky gnome, you have a natural comfort with heights and climbing that frankly terrifies other gnomes.',
        abilityBonuses: [{ ability: 'dexterity', bonus: 2 }],
        traits: [
          {
            id: 'fearless-climber',
            name: 'Fearless Climber',
            description: 'You have a climb speed equal to your walking speed.',
          },
          {
            id: 'friend-of-leaf-and-bough',
            name: 'Friend of Leaf and Bough',
            description:
              'You are extremely at home in a forest setting. You have proficiency with Nature, Survival, and Animal Handling.',
          },
          {
            id: 'nimble-landing',
            name: 'Nimble Landing',
            description:
              'As a reaction, you can cast Feather Fall on yourself without expending a spell slot. You can do this twice per long rest.',
          },
        ],
      },
      {
        id: 'night-gnome',
        name: 'Night Gnome',
        description:
          'Night gnomes are a secretive tribe of nocturnal gnomes. They are adapted for life without light and tend to make their homes in caves, sewers, or the dark, shadowy depths of the thickest forests. They have dark gray skin, dark gray hair, and pale silver eyes.',
        abilityBonuses: [
          { ability: 'wisdom', bonus: 1 },
          { ability: 'dexterity', bonus: 1 },
        ],
        traits: [
          {
            id: 'melt-into-shadow',
            name: 'Melt into Shadow',
            description:
              'As an action, you can turn invisible when in darkness or shadow. Your invisibility ends if you attack, make a damage roll, or force someone to make a saving throw.',
          },
          {
            id: 'blindsight',
            name: 'Blindsight',
            description:
              'If you are able to hear, you are aware of the location of any hidden or invisible creature within 30 feet of you. You have advantage on all perception checks involving sound and cannot be surprised by a hidden or invisible creature within 30 feet of you.',
          },
          {
            id: 'shadow-step',
            name: 'Shadow Step',
            description:
              'As a bonus action, you can cast Misty Step without expending a spell slot. You can do this twice per long rest.',
          },
        ],
      },
    ],
    source: 'ignus',
  },

  // =============================================================================
  // DRAGONBORN - Ignus version with Child of Enclave / Child of Banished
  // =============================================================================
  {
    id: 'dragonborn',
    name: 'Dragonborn',
    description:
      'The Dragonborn of Ignus once lived in harmony with all species and served as liaisons between dragonkind and others. During The Destruction, the various breeds of dragons split into factions and the dragonborn followed suit.',
    abilityBonuses: [
      { ability: 'strength', bonus: 2 },
      { ability: 'charisma', bonus: 1 },
    ],
    age: 'Young dragonborn grow quickly. They walk hours after hatching, attain the size of a 10-year-old human by age 3, and reach adulthood by 15. They live to be around 80.',
    size: 'medium',
    sizeDescription:
      'Dragonborn are taller and heavier than humans, standing well over 6 feet tall and averaging almost 250 pounds.',
    speed: 30,
    languages: ['Common', 'Draconic'],
    traits: [
      {
        id: 'draconic-ancestry',
        name: 'Draconic Ancestry',
        description:
          'You have draconic ancestry. Your subspecies determines what type. Your breath weapon and damage resistance are determined by the dragon type.',
      },
      {
        id: 'breath-weapon',
        name: 'Breath Weapon',
        description:
          'You can use your action to exhale destructive energy. Each creature in the area must make a saving throw. The DC equals 8 + your Constitution modifier + your proficiency bonus. A creature takes 2d6 damage on a failed save, half on success. Damage increases to 3d6 at 6th level, 4d6 at 11th level, and 5d6 at 16th level. Recharges on short or long rest.',
      },
      {
        id: 'damage-resistance',
        name: 'Damage Resistance',
        description: 'You have resistance to the damage type associated with your draconic ancestry.',
      },
    ],
    subraces: [
      {
        id: 'child-of-enclave',
        name: 'Child of the Enclave',
        description:
          'Descendants of warriors who served the victorious Enclave dragon tribes, the Children of the Enclave often hold positions of power and influence in society. They are educated, refined, and well respected. Ancestry options: Black (Acid), Blue (Lightning), Copper (Acid), Gold (Fire), Green (Poison).',
        abilityBonuses: [{ ability: 'intelligence', bonus: 1 }],
        traits: [
          {
            id: 'educated-guess',
            name: 'Educated Guess',
            description:
              'Because of your schooling, you have advantage on History, Religion, and Arcana checks.',
          },
          {
            id: 'enclave-training',
            name: 'Enclave Training',
            description:
              'You have proficiency with crossbows, shortbows, and longbows as well as with light armor.',
          },
          {
            id: 'issued-challenge',
            name: 'Issued Challenge',
            description:
              'As a bonus action, you issue a challenge of single combat to any one creature, compelling that creature to engage you one-on-one. The challenged target has disadvantage on all attack rolls against any target other than you. Uses equal to proficiency bonus, recharges on short rest.',
          },
        ],
      },
      {
        id: 'child-of-banished',
        name: 'Child of the Banished',
        description:
          'Shunned by other dragonborn, the Children of the Banished are descended from the soldiers who served the now Banished dragons. They scrape out an existence as mercenaries, merchants, and muscle for hire. Ancestry options: Brass (Fire), Bronze (Lightning), Red (Fire), Silver (Cold), White (Cold).',
        abilityBonuses: [{ ability: 'strength', bonus: 1 }],
        traits: [
          {
            id: 'hard-knock-life',
            name: 'Hard Knock Life',
            description:
              "Because you've often had to scrape by however possible, you have advantage on Survival, Medicine, and Deception checks.",
          },
          {
            id: 'banished-training',
            name: 'Banished Training',
            description:
              'You have proficiency with martial weapons as well as light, medium, and heavy armor.',
          },
          {
            id: 'clapback',
            name: 'Clapback',
            description:
              'As a reaction to being hit by a melee attack, you can make one melee weapon attack with advantage against the attacker. Uses equal to proficiency bonus, recharges on short rest.',
          },
        ],
      },
    ],
    source: 'ignus',
  },

  // =============================================================================
  // SKYBORN - New Ignus-only species
  // =============================================================================
  {
    id: 'skyborn',
    name: 'Skyborn',
    description:
      'The skies of Ignus are home to a number of airborne creatures but none are as quick and clever as the Skyborn. Skyborn resemble birds in many ways: covered in feathers, with sharp talons, beaked faces, and huge sweeping wings. They are well-regarded trackers, swift messengers, and fierce fighters.',
    abilityBonuses: [{ ability: 'dexterity', bonus: 2 }],
    age: 'Skyborn mature slightly faster than humans, reaching maturity at around age 10 but have a similar lifespan.',
    size: 'medium',
    sizeDescription:
      'Skyborn can be between 3 and 6 feet tall depending on their subspecies. They have thin, lightweight bodies that weigh between 50 and 100 pounds.',
    speed: 30,
    languages: ['Common', 'Skyborn'],
    traits: [
      {
        id: 'flight',
        name: 'Flight',
        description:
          "You have a base flying speed of 45 feet. To use this speed, you can't be wearing medium or heavy armor.",
      },
    ],
    subraces: [
      {
        id: 'clan-corva',
        name: 'Clan Corva (Raven)',
        description:
          'The Skyborn of Clan Corva are among the most commonly found Skyborn subspecies on Ignus due to their willingness to associate with non-avian species. They are renowned for being dangerously clever.',
        abilityBonuses: [{ ability: 'intelligence', bonus: 2 }],
        traits: [
          {
            id: 'beak-and-talons-corva',
            name: 'Beak and Talons',
            description:
              'Your beak and talons are natural weapons, which you can use to make unarmed strikes. If you hit, you deal 1d4 + your Dexterity modifier slashing damage.',
          },
          {
            id: 'clever',
            name: 'Clever',
            description:
              'You have proficiency in the Persuasion, Deception, Perception, and Investigation skills. You can also choose one of these skills and gain expertise in it.',
          },
          {
            id: 'never-forget-a-face',
            name: 'Never Forget a Face',
            description:
              "Corvans are really good with faces. Once they see a person's face, they never forget it, even if it's been decades. You have advantage on perception checks to see through disguises if you've ever seen the person's real face.",
          },
        ],
      },
      {
        id: 'clan-striga',
        name: 'Clan Striga (Owl)',
        description:
          'The Skyborn of Clan Striga tend to stick to the wild spaces of Ignus. They are nocturnal by nature and often quite shy. There is no better spy than a Striga spy.',
        abilityBonuses: [{ ability: 'wisdom', bonus: 2 }],
        traits: [
          {
            id: 'beak-and-talons-striga',
            name: 'Beak and Talons',
            description:
              'Your beak and talons are natural weapons, which you can use to make unarmed strikes. If you hit, you deal 1d4 + your Dexterity modifier slashing damage.',
          },
          {
            id: 'superior-darkvision',
            name: 'Superior Darkvision',
            description:
              'Accustomed to hunting at night, you have superior vision in dark and dim conditions. You can see in dim light within 120 feet of you as if it were bright light, and in darkness as if it were dim light.',
          },
          {
            id: 'shadow-in-the-night',
            name: 'A Shadow in the Night',
            description:
              'You are proficient in Stealth. Additionally, when flying, you may roll 1d10 and add the result to your stealth rolls.',
          },
        ],
      },
      {
        id: 'clan-rapta',
        name: 'Clan Rapta (Raptor)',
        description:
          'The Skyborn of Clan Rapta are fierce-eyed warriors without parallel. They are swift, tenacious, and deadly, generally larger than their Corva or Striga cousins.',
        abilityBonuses: [
          { ability: 'dexterity', bonus: 1 },
          { ability: 'strength', bonus: 1 },
        ],
        traits: [
          {
            id: 'beak-and-talons-rapta',
            name: 'Beak and Talons',
            description:
              'Your beak and talons are natural weapons, which you can use to make unarmed strikes. If you hit, you deal 1d4 + your Strength modifier slashing damage.',
          },
          {
            id: 'zephyr-speed',
            name: 'Zephyr Speed',
            description: 'Your base flying speed is 65 feet instead of 45 feet.',
          },
          {
            id: 'eagles-eye',
            name: "Eagle's Eye",
            description: 'You have advantage on all perception checks involving sight.',
          },
        ],
      },
      {
        id: 'clan-catha',
        name: 'Clan Catha (Vulture)',
        description:
          'The Skyborn of Clan Catha are one of the most misunderstood species on Ignus. The largest of the Skyborn, they often tower over other species with wingspans exceeding 20 feet. Many fear them due to their association with death, but they are comfortable with their role in society.',
        abilityBonuses: [
          { ability: 'constitution', bonus: 1 },
          { ability: 'strength', bonus: 1 },
        ],
        traits: [
          {
            id: 'beak-and-talons-catha',
            name: 'Beak and Talons',
            description:
              'Your beak and talons are natural weapons, which you can use to make unarmed strikes. If you hit, you deal 1d4 + your Strength modifier slashing damage.',
          },
          {
            id: 'iron-stomach',
            name: 'Iron Stomach',
            description: 'You are immune to poison and disease.',
          },
          {
            id: 'windrest',
            name: 'Windrest',
            description:
              "Catha don't need the same amount of sleep as other species. You gain a long rest with only four hours of semi-conscious activity. You can achieve this rest while airborne, locking your wings and instinctively riding the winds. You may make perception checks while in this state but at disadvantage.",
          },
        ],
      },
    ],
    source: 'ignus',
  },

  // =============================================================================
  // IGNAN - New Ignus-only species
  // =============================================================================
  {
    id: 'ignan',
    name: 'Ignan',
    description:
      "The people of Ignus believe that Ignus itself is an ancient being of immense power who slumbers through the millennia. The titans are children of Ignus, but they aren't the only ones. Ignans are the most humanlike of Ignus-born beings, though they often don't look very human - their tie to Ignus and its elemental powers tends to express itself in their physical appearance.",
    abilityBonuses: [{ ability: 'wisdom', bonus: 2 }],
    age: 'Ignans mature at a very similar rate to humans, reaching maturity at around 25. They can live 350 to almost 500 years.',
    size: 'medium',
    sizeDescription:
      'Ignans range in size from a relatively small 3 feet to over 7 feet tall depending on their subspecies.',
    speed: 35,
    languages: ['Common', 'Primordial'],
    traits: [
      {
        id: 'elemental-nature',
        name: 'Elemental Nature',
        description:
          'Your creature type is Elemental. You are tied to Ignus and its elemental powers.',
      },
      {
        id: 'ignan-nature',
        name: 'Ignan Nature',
        description:
          'Born of the elemental nature of Ignus, you are resistant to Poison, Acid, Fire, Cold, Lightning, and Thunder damage.',
      },
      {
        id: 'commune',
        name: 'Commune',
        description:
          "You don't need to sleep as much as other species, instead finding rest and rejuvenation in the arms of Ignus. You can finish a long rest in 2 hours but those hours are spent completely catatonic as though in a coma. You cannot be woken during those two hours. For this reason, most ignans never travel alone. You can, instead, choose to sleep normally rather than commune. If you choose to sleep, you can get a long rest in 6 hours.",
      },
    ],
    subraces: [
      {
        id: 'people-of-tempest',
        name: 'People of the Tempest',
        description:
          'The People of the Tempest, or "Stormies" as they\'re commonly called, are an uncommon sight in civil society. Their connection with the winds and storms of Ignus is often reflected in their personalities and they have a reputation of being capricious, quick-tempered, and hot-headed. Their physical characteristics tend to reflect the aspect of the storm they most embody, and all are covered in an odd mixture of scales, skin, and sometimes fur. Their eyes resemble faceted gems that flash and spark with lightning.',
        abilityBonuses: [{ ability: 'dexterity', bonus: 1 }],
        traits: [
          {
            id: 'at-home-in-sky',
            name: 'At Home in the Sky',
            description:
              'You are a creature of the sky. Once per short or long rest, you can cast Fly on yourself as a free action. Additionally, you are immune to falling damage.',
          },
          {
            id: 'one-with-storm',
            name: 'One With the Storm',
            description:
              'You are the embodiment of the storm and the wind responds to your call. You are able to cast Call Lightning or Thunder Wave without expending a spell slot. You can use your One With the Storm ability a number of times equal to your proficiency bonus. All uses reset after a long rest.',
          },
          {
            id: 'tempest-ignan-magic',
            name: 'Tempest Ignan Magic',
            description:
              'Thanks to your Tempest Ignan ancestry, you know the Shocking Grasp cantrip which you can cast at will as a bonus action regardless of whether or not you are a spellcaster. You also know Ice Storm, Misty Step, and Shatter and can cast each once per long rest without expending a spell slot. Wisdom is your spellcasting ability for these spells.',
            spellGrants: {
              cantrips: ['shocking-grasp'],
              spells: [
                { spellId: 'ice-storm', level: 1, usesPerRest: 1, restType: 'long' },
                { spellId: 'misty-step', level: 1, usesPerRest: 1, restType: 'long' },
                { spellId: 'shatter', level: 1, usesPerRest: 1, restType: 'long' },
              ],
              spellcastingAbility: 'wisdom',
            },
          },
        ],
      },
      {
        id: 'people-of-wild',
        name: 'People of the Wild',
        description:
          'The People of the Wild, or "Wilders" as they\'re commonly known, are the most populous and gregarious of the ignan species. They tend towards a lighthearted, capricious nature and can always be counted on to join into a party. They resemble gnomes or halflings at first glance but have distinctly animalistic features upon closer examination. Their large, gem-like eyes don\'t have pupils but are filled with floating motes of light like tiny fireflies, changing color with their moods.',
        abilityBonuses: [{ ability: 'charisma', bonus: 1 }],
        traits: [
          {
            id: 'environmental-adept',
            name: 'Environmental Adept',
            description:
              'You are in tune with the world around you and equally at home in all its environments. You have truesight up to 60 feet. You can breathe underwater and have a swim speed equal to your walking speed. You also have a climbing speed equal to your walking speed and you can ignore difficult terrain.',
          },
          {
            id: 'one-with-wild',
            name: 'One With The Wild',
            description:
              'You are one with the living world and it responds to your touch. You are able to cast Speak with Animals or Speak with Plants at will. You can use your One With the Wild ability a number of times equal to your proficiency bonus. All uses reset after a long rest.',
          },
          {
            id: 'wild-ignan-magic',
            name: 'Wild Ignan Magic',
            description:
              'Thanks to your Wild Ignan ancestry, you know the Druidcraft cantrip which you can cast at will as a bonus action regardless of whether or not you are a spellcaster. You also know Bane, Bless, Inflict Wounds, and Cure Wounds and can cast each once per long rest without expending a spell slot. Wisdom is your spellcasting ability for these spells.',
            spellGrants: {
              cantrips: ['druidcraft'],
              spells: [
                { spellId: 'bane', level: 1, usesPerRest: 1, restType: 'long' },
                { spellId: 'bless', level: 1, usesPerRest: 1, restType: 'long' },
                { spellId: 'inflict-wounds', level: 1, usesPerRest: 1, restType: 'long' },
                { spellId: 'cure-wounds', level: 1, usesPerRest: 1, restType: 'long' },
              ],
              spellcastingAbility: 'wisdom',
            },
          },
        ],
      },
      {
        id: 'people-of-deep',
        name: 'People of the Deep',
        description:
          'Deep Ignans, or "Dusties" as they\'re commonly called, prefer the dark, deep places of the world. They are imposing figures, often towering above the crowd in the streets of Ignan cities. Despite their size, they walk and speak softly, and are often considered good, hard workers. Their skin is thick and leathery and covered with patches of chitinous plating, sometimes even forming full shells on their backs. Their eyes look like faceted black gemstones.',
        abilityBonuses: [{ ability: 'strength', bonus: 1 }],
        traits: [
          {
            id: 'plated',
            name: 'Plated',
            description:
              'Due to your thick skin and chitinous plates, your base AC is 12 instead of the usual 10.',
          },
          {
            id: 'at-home-in-dark',
            name: 'At Home in the Dark',
            description:
              'You are adapted to life underground. You have darkvision up to 120 feet. You also have tremorsense up to 60 feet.',
          },
          {
            id: 'deep-stealth',
            name: 'Deep Stealth',
            description:
              "In the dark, any sound you make could be your last. While in dim light or darkness, you have advantage on stealth checks against being heard and can take the Hide action as a bonus action.",
          },
          {
            id: 'deep-ignan-magic',
            name: 'Deep Ignan Magic',
            description:
              'Thanks to your Deep Ignan ancestry, you know the Message cantrip which you can cast as a bonus action regardless of whether or not you are a spellcaster. You also know the following spells and can cast them once per long rest without expending a spell slot: Meld Into Stone, Stone Shape, Silence, Darkness. Wisdom is your spellcasting ability for these spells.',
            spellGrants: {
              cantrips: ['message'],
              spells: [
                { spellId: 'meld-into-stone', level: 1, usesPerRest: 1, restType: 'long' },
                { spellId: 'stone-shape', level: 1, usesPerRest: 1, restType: 'long' },
                { spellId: 'silence', level: 1, usesPerRest: 1, restType: 'long' },
                { spellId: 'darkness', level: 1, usesPerRest: 1, restType: 'long' },
              ],
              spellcastingAbility: 'wisdom',
            },
          },
        ],
      },
      {
        id: 'people-of-peaks',
        name: 'People of the Peaks',
        description:
          'A tough, hardy people, Peak Ignans, or "Peakies" as they\'re often called, prefer the high, cold, arid places of the world. They are known to be direct, honest, and no-nonsense. Generally between 5-6 feet tall, they are apelike and covered in shaggy fur ranging from dark red to dazzling white. They have large, nimble hands, surprisingly sharp teeth, and eyes that resemble smooth, polished gemstones glowing with a pulsing interior light.',
        abilityBonuses: [{ ability: 'constitution', bonus: 1 }],
        traits: [
          {
            id: 'furry',
            name: 'Furry',
            description:
              'Your thick coat of fur acts like a coat of insulated armor. You are immune to cold damage and resistant to bludgeoning damage.',
          },
          {
            id: 'surefooted',
            name: 'Surefooted',
            description:
              'Adapted to life on the peaks, you have advantage on all Dexterity saving throws and ability checks.',
          },
          {
            id: 'vertically-unchallenged',
            name: 'Vertically Unchallenged',
            description:
              "You can't live on the heights if you're afraid of falling. Once per short or long rest, you can cast Feather Fall on yourself as a free action.",
          },
          {
            id: 'peak-ignan-magic',
            name: 'Peak Ignan Magic',
            description:
              'Thanks to your Peak Ignan ancestry, you know the Ray of Frost cantrip which you can cast as a bonus action regardless of whether or not you are a spellcaster. You also know Meld Into Stone, Cone of Cold, and Spider Climb and can cast each once per long rest without expending a spell slot. Wisdom is your spellcasting ability for these spells.',
            spellGrants: {
              cantrips: ['ray-of-frost'],
              spells: [
                { spellId: 'meld-into-stone', level: 1, usesPerRest: 1, restType: 'long' },
                { spellId: 'cone-of-cold', level: 1, usesPerRest: 1, restType: 'long' },
                { spellId: 'spider-climb', level: 1, usesPerRest: 1, restType: 'long' },
              ],
              spellcastingAbility: 'wisdom',
            },
          },
        ],
      },
    ],
    source: 'ignus',
  },

  // =============================================================================
  // HALF-ELF - Ignus version
  // =============================================================================
  {
    id: 'half-elf',
    name: 'Half-Elf',
    description:
      'Half-elves combine what some say are the best qualities of their elf and human parents: human curiosity, inventiveness, and ambition tempered by the refined senses, love of nature, and artistic tastes of the elves.',
    abilityBonuses: [{ ability: 'charisma', bonus: 2 }],
    age: 'Half-elves mature at the same rate humans do and reach adulthood around the age of 20. They live much longer than humans, however, often exceeding 180 years.',
    size: 'medium',
    sizeDescription:
      'Half-elves are about the same size as humans, ranging from 5 to 6 feet tall.',
    speed: 30,
    languages: ['Common', 'Elvish'],
    languageChoice: 1,
    traits: [
      {
        id: 'darkvision',
        name: 'Darkvision',
        description:
          'Thanks to your elf blood, you have superior vision in dark and dim conditions. You can see in dim light within 60 feet of you as if it were bright light, and in darkness as if it were dim light.',
      },
      {
        id: 'fey-ancestry',
        name: 'Fey Ancestry',
        description:
          "You have advantage on saving throws against being charmed, and magic can't put you to sleep.",
      },
      {
        id: 'skill-versatility',
        name: 'Skill Versatility',
        description: 'You gain proficiency in two skills of your choice.',
      },
      {
        id: 'ability-score-choice',
        name: 'Ability Score Flexibility',
        description:
          'In addition to your Charisma increase, two other ability scores of your choice increase by 1.',
      },
    ],
    source: 'ignus',
  },

  // =============================================================================
  // HALF-ORC - Ignus version
  // =============================================================================
  {
    id: 'half-orc',
    name: 'Half-Orc',
    description:
      'Half-orcs exhibit a blend of orcish and human characteristics, and their appearance varies widely. Some half-orcs are blessed with the strength and tenacity of their orc heritage, while others favor their human side.',
    abilityBonuses: [
      { ability: 'strength', bonus: 2 },
      { ability: 'constitution', bonus: 1 },
    ],
    age: 'Half-orcs mature a little faster than humans, reaching adulthood around age 14. They age noticeably faster and rarely live longer than 75 years.',
    size: 'medium',
    sizeDescription:
      'Half-orcs are somewhat larger and bulkier than humans, and they range from 5 to well over 6 feet tall.',
    speed: 30,
    languages: ['Common', 'Orc'],
    traits: [
      {
        id: 'darkvision',
        name: 'Darkvision',
        description:
          'Thanks to your orc blood, you have superior vision in dark and dim conditions. You can see in dim light within 60 feet of you as if it were bright light, and in darkness as if it were dim light.',
      },
      {
        id: 'menacing',
        name: 'Menacing',
        description: 'You gain proficiency in the Intimidation skill.',
      },
      {
        id: 'relentless-endurance',
        name: 'Relentless Endurance',
        description:
          "When you are reduced to 0 hit points but not killed outright, you can drop to 1 hit point instead. You can't use this feature again until you finish a long rest.",
      },
      {
        id: 'savage-attacks',
        name: 'Savage Attacks',
        description:
          "When you score a critical hit with a melee weapon attack, you can roll one of the weapon's damage dice one additional time and add it to the extra damage of the critical hit.",
      },
    ],
    source: 'ignus',
  },

  // =============================================================================
  // TIEFLING - Ignus version
  // =============================================================================
  {
    id: 'tiefling',
    name: 'Tiefling',
    description:
      'Tieflings are derived from human bloodlines, and in the broadest possible sense, they still look human. However, their infernal heritage has left a clear imprint on their appearance. They have large horns, tails, and eyes of solid colors without visible pupils.',
    abilityBonuses: [
      { ability: 'intelligence', bonus: 1 },
      { ability: 'charisma', bonus: 2 },
    ],
    age: 'Tieflings mature at the same rate as humans but live a few years longer.',
    size: 'medium',
    sizeDescription: 'Tieflings are about the same size and build as humans.',
    speed: 30,
    languages: ['Common', 'Infernal'],
    traits: [
      {
        id: 'darkvision',
        name: 'Darkvision',
        description:
          'Thanks to your infernal heritage, you have superior vision in dark and dim conditions. You can see in dim light within 60 feet of you as if it were bright light, and in darkness as if it were dim light.',
      },
      {
        id: 'hellish-resistance',
        name: 'Hellish Resistance',
        description: 'You have resistance to fire damage.',
      },
      {
        id: 'infernal-legacy',
        name: 'Infernal Legacy',
        description:
          'You know the Thaumaturgy cantrip. When you reach 3rd level, you can cast the Hellish Rebuke spell as a 2nd-level spell once with this trait and regain the ability to do so when you finish a long rest. When you reach 5th level, you can cast the Darkness spell once with this trait and regain the ability to do so when you finish a long rest. Charisma is your spellcasting ability for these spells.',
        spellGrants: {
          cantrips: ['thaumaturgy'],
          spells: [
            { spellId: 'hellish-rebuke', level: 3, usesPerRest: 1, restType: 'long' },
            { spellId: 'darkness', level: 5, usesPerRest: 1, restType: 'long' },
          ],
          spellcastingAbility: 'charisma',
        },
      },
      {
        id: 'creature-type-fiend',
        name: 'Fiendish Nature',
        description: 'Your creature type is Fiend.',
      },
    ],
    source: 'ignus',
  },

  // =============================================================================
  // HUMAN - Ignus version
  // =============================================================================
  {
    id: 'human',
    name: 'Human',
    description:
      "It's hard to make generalizations about humans, but they are the most adaptable and versatile of the common species. Whatever drives them, humans are the innovators, the achievers, and the pioneers of the worlds.",
    abilityBonuses: [
      { ability: 'strength', bonus: 1 },
      { ability: 'dexterity', bonus: 1 },
      { ability: 'constitution', bonus: 1 },
      { ability: 'intelligence', bonus: 1 },
      { ability: 'wisdom', bonus: 1 },
      { ability: 'charisma', bonus: 1 },
    ],
    age: 'Humans reach adulthood in their late teens and live less than a century.',
    size: 'medium',
    sizeDescription:
      'Humans vary widely in height and build, from barely 5 feet to well over 6 feet tall.',
    speed: 30,
    languages: ['Common'],
    languageChoice: 1,
    traits: [
      {
        id: 'versatile',
        name: 'Versatile',
        description:
          'Humans are adaptable and ambitious, learning quickly and excelling in whatever path they choose.',
      },
    ],
    source: 'ignus',
  },
];
